import React, { FC, useEffect, useState } from 'react';
import EmptyState from '@atlaskit/empty-state';
import { ExperienceSuccess as UFOExperienceSuccess } from '@atlassian/ufo';
import { useLocation } from 'react-router-dom';

import {
  useLegacySearchRedirectController,
  useNavigationController,
  useOrgUserController,
} from '@/controllers';
import Homepage from '@/components/composed/homepage';
import { ExperienceErrorBoundary } from '@/components/primitive/error-boundary';
import { UFOExperienceContext, useRestartUFOComponentExperience } from '@/common/ufo';
import { homepageViewExperience } from '@/common/ufo/experiences';

import * as styles from './styles';

const HomepageView: FC = () => (
  <ExperienceErrorBoundary>
    <UFOExperienceSuccess experience={homepageViewExperience} />
    <Homepage />
  </ExperienceErrorBoundary>
);

const HomepageViewInstrumented: typeof HomepageView = props => {
  useRestartUFOComponentExperience(homepageViewExperience, []);

  return (
    <UFOExperienceContext.Provider value={homepageViewExperience}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <HomepageView {...props} />
    </UFOExperienceContext.Provider>
  );
};

const Main: typeof HomepageView = props => {
  useLegacySearchRedirectController();

  const { search } = useLocation();
  const { onNavigation } = useNavigationController();
  const orgUser = useOrgUserController();
  const [isHomepageView, setIsHomepageView] = useState<boolean>(false);

  useEffect(() => {
    if (orgUser.isLoading) return;

    // TODO: In M3: https://hello.atlassian.net/browse/TOME-1709
    // We will handle sub-group / group case:
    if (!orgUser.teams?.length) {
      onNavigation({
        pathname: '/search',
        search,
      });
      return;
    }

    // In M1: https://hello.atlassian.net/browse/TOME-1575
    // We only redirect when the user belongs to a single team
    if (orgUser.teams.length === 1) {
      onNavigation({
        pathname: `/team/${orgUser.teams[0].publicId}/dashboard`,
        search: `${search ? `${search}&` : '?'}showDrafts=true`,
      });
    } else {
      // In M2: https://hello.atlassian.net/browse/TOME-1708
      // We handle multiple teams case:
      setIsHomepageView(true);
    }
  }, [orgUser, onNavigation, search]);

  if (isHomepageView) {
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <HomepageViewInstrumented {...props} />;
  }

  return (
    <div css={styles.pageLoaderStyles}>
      <div css={[styles.logoStyles]}>
        <EmptyState header="" description="" imageUrl="/static/logo-raw.svg" />
      </div>
    </div>
  );
};

export default Main;
